// Adapts an all-purpose cluster's Auto Tune recommendations into a single
// DAG node so the cluster drawer can reuse the jobs ``AutoTuneJobDag``
// canvas. A cluster carries at most one ``resize`` (Worker Sizing) and one
// ``auto_terminate`` (Auto Termination) recommendation; both are folded
// into one node showing the cluster's Current → Recommended state.
import type {
  AutoTuneTaskNodeData,
  ClusterSpec,
} from "@/pages/jobs/auto-tune/AutoTuneJobDag.types";
import type { ClusterAutoTuneRecommendation } from "./types";

export interface ClusterDagContext {
  clusterId: string;
  clusterName: string;
  // Node instance types from the cluster detail — unchanged by the
  // recommendation (resize tunes worker COUNT, not type), surfaced as
  // context so the card reads like a jobs cluster card.
  workerNodeType?: string | null;
  driverNodeType?: string | null;
  // The cluster's live worker sizing from the detail page. Used as the
  // fallback when there's no ``resize`` recommendation, so the card shows
  // the actual worker count on both panels instead of an empty "—".
  minAutoscaleWorkers?: number | null;
  maxAutoscaleWorkers?: number | null;
  workerCount?: number | null;
}

// The recommendation config JSONB is typed ``unknown``; read a numeric
// knob defensively so a missing / malformed value collapses to ``null``
// (the row then hides) rather than rendering "NaN".
const numFromConfig = (config: Record<string, unknown> | undefined, key: string): number | null => {
  const value = config?.[key];
  // Accept real JSON numbers and numeric strings — Postgres JSONB can
  // round-trip ``2`` / ``180`` as ``"2"`` / ``"180"``; anything else
  // (missing or non-numeric) collapses to ``null`` so the row hides.
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// Stitch the cluster's resize + auto_terminate recommendations into one
// ``AutoTuneTaskNodeData``. Returns ``null`` when neither recommendation
// is present, so the drawer can skip the DAG entirely.
export const buildClusterRecommendationNode = (
  recommendations: ClusterAutoTuneRecommendation[],
  ctx: ClusterDagContext
): AutoTuneTaskNodeData | null => {
  const resize = recommendations.find(rec => rec.rec_type === "resize");
  const autoTerminate = recommendations.find(rec => rec.rec_type === "auto_terminate");
  if (!resize && !autoTerminate) return null;

  // Worker Sizing — ``resize`` carries ``{min_workers, max_workers}`` in
  // both config snapshots; the min change is the headline (e.g. 2 → 1).
  // With no resize rec, fall back to the cluster's live sizing (autoscale
  // min/max, else fixed worker count) so both panels show the real count.
  const clusterMin = ctx.minAutoscaleWorkers ?? ctx.workerCount ?? null;
  const clusterMax = ctx.maxAutoscaleWorkers ?? ctx.workerCount ?? null;
  const currentMin = numFromConfig(resize?.current_config, "min_workers") ?? clusterMin;
  const currentMax = numFromConfig(resize?.current_config, "max_workers") ?? clusterMax;
  const proposedMin = numFromConfig(resize?.recommended_config, "min_workers") ?? currentMin;
  const proposedMax = numFromConfig(resize?.recommended_config, "max_workers") ?? currentMax;

  // Auto Termination — ``auto_terminate`` carries
  // ``{auto_termination_minutes}`` (e.g. 180 → 45).
  const currentAutoTerm = numFromConfig(autoTerminate?.current_config, "auto_termination_minutes");
  const proposedAutoTerm = numFromConfig(
    autoTerminate?.recommended_config,
    "auto_termination_minutes"
  );

  const worker = ctx.workerNodeType ?? null;
  const driver = ctx.driverNodeType ?? worker;

  const current: ClusterSpec = {
    instance: worker ?? "—",
    workerInstance: worker,
    driverInstance: driver,
    min: currentMin,
    max: currentMax,
    autoTerminationMinutes: currentAutoTerm,
    // DBU price snapshot from the resize rec; ``null`` hides the cost row.
    costPerHr: resize?.current_cost_per_hr_dbu ?? null,
  };
  // ``proposedMin`` / ``proposedMax`` already fall back to the current
  // values above, so a single-sided rec still shows the unchanged knob.
  const proposed: ClusterSpec = {
    instance: worker ?? "—",
    workerInstance: worker,
    driverInstance: driver,
    min: proposedMin,
    max: proposedMax,
    autoTerminationMinutes: proposedAutoTerm ?? currentAutoTerm,
    costPerHr: resize?.recommended_cost_per_hr_dbu ?? resize?.current_cost_per_hr_dbu ?? null,
    // Accent the proposed minimum in green only when the resize rec
    // actually LOWERS it — a positive (downsizing) Worker Sizing
    // recommendation. ``< `` (not ``!==``) so an unchanged or larger
    // minimum is never highlighted.
    highlightMinWorkers:
      typeof proposedMin === "number" && typeof currentMin === "number" && proposedMin < currentMin,
  };

  const reasoning =
    [resize, autoTerminate]
      .map(rec => rec?.recommendation_summary || rec?.reasoning)
      .filter((text): text is string => Boolean(text))
      .join("\n\n") || null;

  return {
    id: ctx.clusterName || ctx.clusterId,
    taskKey: ctx.clusterId,
    current,
    proposed,
    // Nothing is applied to Databricks for clusters (the toggle only
    // records config + audit), so the card always reads as the
    // recommended state regardless of the sub-toggle positions.
    status: "recommended",
    // Populated for node-shape parity with the jobs node (the field is
    // required on ``AutoTuneTaskNodeData``); this read-only cluster DAG
    // doesn't consume it — card chrome is driven by ``status``.
    enabled: [resize, autoTerminate].some(rec => rec?.enabled),
    reasoning,
    // The drawer has no date range to compute a realized monthly figure,
    // so the savings strip is intentionally suppressed — the card focuses
    // on the recommended config deltas.
    monthlySavingsUsd: null,
    monthlySavingsMaxUsd: null,
    monthlyDbuSavingsUsd: null,
    toggling: false,
    locked: false,
    lockedMessage: null,
  };
};
